import React, { useState, useEffect, useRef } from "react";

const ImageGallery = ({ images = [], productName = "Product", discount = 0 }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [imgError, setImgError] = useState({});
  const autoScrollRef = useRef(null);

  const startAutoScroll = (imgCount) => {
    clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setSelectedImage((prev) => (prev + 1) % imgCount);
    }, 7000);
  };

  useEffect(() => {
    if (images.length <= 1) return;
    startAutoScroll(images.length);
    return () => clearInterval(autoScrollRef.current);
  }, [images.length]);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (images.length <= 1) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      setSelectedImage((prev) => (prev + 1) % images.length);
      startAutoScroll(images.length);
    } else if (diff < -50) {
      setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
      startAutoScroll(images.length);
    }
  };

  const handleImageClick = () => {
    if (images.length <= 1) return;
    if (window.innerWidth < 1024) return;
    setSelectedImage((prev) => (prev + 1) % images.length);
    startAutoScroll(images.length);
  };

  return (
    <div className="lg:sticky lg:top-4">
      <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg">
        {discount > 0 && (
          <div className="absolute left-4 top-4 z-10 rounded-full bg-[#ea580c] px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-md">
            {discount}% OFF
          </div>
        )}
        <div
          className={`flex h-[320px] sm:h-[400px] lg:h-[min(480px,52vh)] items-center justify-center p-6 lg:p-8 ${images.length > 1 ? "lg:cursor-pointer select-none" : ""}`}
          onClick={handleImageClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          title={
            images.length > 1
              ? window.innerWidth >= 1024
                ? "Click to see next image"
                : undefined
              : undefined
          }
        >
          {images[selectedImage] && !imgError[selectedImage] ? (
            <img
              src={images[selectedImage]}
              alt={`${productName} - view ${selectedImage + 1}`}
              className="h-full w-full object-contain transition-all duration-500 pointer-events-none"
              onError={() =>
                setImgError((prev) => ({ ...prev, [selectedImage]: true }))
              }
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-gray-300">
              <svg
                className="h-24 w-24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2 1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
                />
              </svg>
              <span className="text-sm font-medium">No Image</span>
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/90 px-3 py-2 shadow-md backdrop-blur">
            {images.map((_, index) => (
              <span
                key={index}
                className={`h-1.5 rounded-full transition-all ${selectedImage === index ? "w-5 bg-primary-600" : "w-1.5 bg-gray-300"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
